import { useState } from "react";
import { styles } from "../styles";
import { contactTypes } from "../utils/constants";
import AuditTrail from "../components/AuditTrail";
import BackButton from "../components/BackButton";

export default function ContactsPage({
  customer,
  location,
  actions,
  user,
  role,
  setSelectedContactLocation,
  setSelectedContactCustomer,
}) {
  const isCustomerMode = !location;
  // Source from live customer prop; never the click-time snapshot.
  const currentLocation = !isCustomerMode
    ? customer?.locations?.find((l) => l.id === location?.id) || null
    : null;

  const targetName = isCustomerMode ? customer?.name : currentLocation?.name;
  const contactsList = isCustomerMode
    ? customer?.contacts || []
    : currentLocation?.contacts || [];

  const canEdit = role === "admin" || role === "manager";
  const canViewAudit = role === "admin" || role === "manager";
  const isAdmin = role === "admin";

  const [contactType, setContactType] = useState("Engineering");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactNotes, setContactNotes] = useState("");
  const [showAddContact, setShowAddContact] = useState(false);
  const [showDeletedContacts, setShowDeletedContacts] = useState(false);
  const [showContactAuditTrail, setShowContactAuditTrail] = useState({});

  const [editingContactId, setEditingContactId] = useState(null);
  const [editType, setEditType] = useState("Engineering");
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const startEditingContact = (contact) => {
    setEditingContactId(contact.id);
    setEditType(contact.type || "Engineering");
    setEditName(contact.name || "");
    setEditPhone(contact.phone || "");
    setEditEmail(contact.email || "");
    setEditNotes(contact.notes || "");
  };

  const updateContact = async () => {
    if (!editName || !editingContactId) return;

    const original = contactsList.find((c) => c.id === editingContactId);
    const auditDetail = `Type: ${original?.type || "-"} → ${editType}\nName: ${original?.name || "-"} → ${editName}\nPhone: ${original?.phone || "-"} → ${editPhone || "-"}\nEmail: ${original?.email || "-"} → ${editEmail || "-"}\nNotes: ${original?.notes || "-"} → ${editNotes || "-"}`;

    await actions.updateContact(editingContactId, {
      type: editType,
      name: editName,
      phone: editPhone,
      email: editEmail,
      notes: editNotes,
      auditTrail: [
        ...(original?.auditTrail || []),
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          action: "Edited Contact",
          details: auditDetail,
          user: user?.email || "unknown",
          timestamp: new Date().toISOString(),
        },
      ],
    });
    setEditingContactId(null);
    setEditName("");
  };

  const addContact = async () => {
    if (!contactName) return;

    const newContact = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: contactType,
      name: contactName,
      phone: contactPhone,
      email: contactEmail,
      notes: contactNotes,
      createdAt: new Date().toISOString(),
      createdBy: user?.email || "unknown",
      deleted: false,
      deletedAt: null,
      deletedBy: null,
      auditTrail: [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          action: "Created Contact",
          details: "Contact was created",
          user: user?.email || "unknown",
          timestamp: new Date().toISOString(),
        },
      ],
    };

    await actions.addContact(
      customer.id,
      isCustomerMode ? null : currentLocation.id,
      newContact
    );

    setContactType("Engineering");
    setContactName("");
    setContactPhone("");
    setContactEmail("");
    setContactNotes("");
  };

  const softDeleteContact = async (contactId) => {
    const confirmed = window.confirm("Delete this contact?");
    if (!confirmed) return;
    await actions.softDeleteContact(contactId, user?.email);
  };

  const restoreContact = async (contactId) => {
    await actions.restoreContact(contactId, user?.email);
  };

  const permanentlyDeleteContact = async (contactId) => {
    const confirmed = window.confirm(
      "Permanently delete this contact?\n\nThis cannot be undone."
    );
    if (!confirmed) return;
    await actions.permanentlyDeleteContact(contactId, user?.email);
  };

  const toggleAuditTrail = (contactId) =>
    setShowContactAuditTrail((prev) => ({
      ...prev,
      [contactId]: !prev[contactId],
    }));

  const activeContacts = contactsList.filter((c) => !c.deleted);
  const deletedContactsList = contactsList.filter((c) => c.deleted);

  const handleBack = () => {
    if (isCustomerMode) {
      setSelectedContactCustomer(null);
    } else {
      setSelectedContactLocation(null);
    }
  };

  if (!isCustomerMode && !currentLocation) {
    return (
      <>
        <BackButton onClick={handleBack} />
        <p style={{ fontSize: "13px", color: "#666", marginTop: "8px" }}>
          Loading building…
        </p>
      </>
    );
  }

  return (
    <>
      <BackButton onClick={handleBack} />

      <h2>{targetName} Contacts</h2>

      {canEdit && (
        <>
          <button
            style={styles.button}
            onClick={() => setShowAddContact(!showAddContact)}
          >
            {showAddContact ? "Hide Add Contact" : "Add Contact"}
          </button>

          {showAddContact && (
            <div style={styles.card}>
              <label>Contact Type: </label>
              <select
                value={contactType}
                onChange={(e) => setContactType(e.target.value)}
              >
                {contactTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <br />
              <input
                style={styles.input}
                placeholder="Name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
              <input
                style={styles.input}
                placeholder="Phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
              <input
                style={styles.input}
                placeholder="Email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
              <textarea
                style={styles.textarea}
                placeholder="Notes"
                value={contactNotes}
                onChange={(e) => setContactNotes(e.target.value)}
              />
              <br />
              <button style={styles.button} onClick={addContact}>
                Add Contact
              </button>
            </div>
          )}
        </>
      )}

      <h3>Saved Contacts</h3>

      {isAdmin && (
        <button
          style={styles.button}
          onClick={() => setShowDeletedContacts(!showDeletedContacts)}
        >
          {showDeletedContacts
            ? "Hide Deleted Contacts"
            : "Show Deleted Contacts"}
        </button>
      )}

      {activeContacts.length === 0 ? (
        <p>No contacts saved yet.</p>
      ) : (
        activeContacts.map((contact) => (
          <div key={contact.id} style={styles.card}>
            {editingContactId === contact.id ? (
              <>
                <label>Contact Type: </label>
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                >
                  {contactTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <br />
                <input
                  style={styles.input}
                  placeholder="Name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
                <input
                  style={styles.input}
                  placeholder="Phone"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                />
                <input
                  style={styles.input}
                  placeholder="Email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
                <textarea
                  style={styles.textarea}
                  placeholder="Notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                />
                <br />
                <button style={styles.button} onClick={updateContact}>
                  Save
                </button>
                <button
                  style={styles.button}
                  onClick={() => {
                    setEditingContactId(null);
                    setEditName("");
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <strong>{contact.type}</strong>
                <br />
                <strong>Name:</strong> {contact.name}
                <br />
                <strong>Phone:</strong> {contact.phone || "-"}
                <br />
                <strong>Email:</strong> {contact.email || "-"}
                <br />
                <strong>Notes:</strong> {contact.notes || "-"}
                <br />
                <span style={{ fontSize: "12px", color: "#666" }}>
                  Added by {contact.createdByName || contact.createdBy || "unknown"}
                </span>

                {canEdit && (
                  <>
                    <br />
                    <button
                      style={styles.button}
                      onClick={() => startEditingContact(contact)}
                    >
                      Edit Contact
                    </button>
                    <button
                      style={styles.button}
                      onClick={() => softDeleteContact(contact.id)}
                    >
                      Delete Contact
                    </button>
                  </>
                )}
                {canViewAudit && (
                  <>
                    <br />
                    <button
                      style={styles.button}
                      onClick={() => toggleAuditTrail(contact.id)}
                    >
                      {showContactAuditTrail[contact.id]
                        ? "Hide Contact Audit Trail"
                        : "Show Contact Audit Trail"}
                    </button>

                    {showContactAuditTrail[contact.id] && (
                      <AuditTrail
                        trail={contact.auditTrail}
                        label="Contact Audit Trail"
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        ))
      )}

      {isAdmin && showDeletedContacts && (
        <>
          <h3>Deleted Contacts</h3>

          {deletedContactsList.length === 0 ? (
            <p>No deleted contacts.</p>
          ) : (
            deletedContactsList.map((contact) => (
              <div key={contact.id} style={styles.card}>
                <strong>{contact.type}</strong>
                <br />
                <strong>Name:</strong> {contact.name}
                <br />
                <strong>Phone:</strong> {contact.phone || "-"}
                <br />
                <strong>Email:</strong> {contact.email || "-"}
                <br />
                <strong>Notes:</strong> {contact.notes || "-"}
                <br />

                <div style={{ fontSize: "12px", color: "#666" }}>
                  Deleted by {contact.deletedBy || "unknown"} on{" "}
                  {contact.deletedAt || "-"}
                  <br />
                  <button
                    style={styles.button}
                    onClick={() => restoreContact(contact.id)}
                  >
                    Restore Contact
                  </button>
                  <button
                    style={styles.button}
                    onClick={() => permanentlyDeleteContact(contact.id)}
                  >
                    Permanently Delete
                  </button>
                  <br />
                  <button
                    style={styles.button}
                    onClick={() => toggleAuditTrail(contact.id)}
                  >
                    {showContactAuditTrail[contact.id]
                      ? "Hide Contact Audit Trail"
                      : "Show Contact Audit Trail"}
                  </button>

                  {showContactAuditTrail[contact.id] && (
                    <AuditTrail
                      trail={contact.auditTrail}
                      label="Contact Audit Trail"
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </>
  );
}
